import {
  collection,
  doc,
  onSnapshot,
  setDoc,
  deleteDoc,
  getDoc,
  Unsubscribe,
} from "firebase/firestore";
import { db } from "@/config/firebase";

export interface UsbController {
  id: string;
  name: string;
  notes?: string;
  // Admin-owned compatibility verdict against the MasterPower-integrated hub.
  // true = known-good, false = known-bad, undefined = unrated.
  compatible?: boolean;
}

export interface UsbHub {
  id: string;
  name: string;
  imageUrl?: string;
  pd: boolean;
  connector: "usb-b" | "usb-c";
  forControllers: string[];
  notes?: string;
}

export interface CameraEntry {
  id: string;
  facing: string;
  megapixels: number;
  resolutionWidth: number;
  resolutionHeight: number;
  rawSupport: boolean;
  focalLengths?: number[];
  sensorSizeWidth?: number;
  sensorSizeHeight?: number;
  lens?: string;
}

export interface UsbDevice {
  id: string;
  manufacturer: string;
  model: string;
  marketingName?: string;
  controller: string;
  androidVersion: string;
  lastSeenAt?: { seconds: number };
  chipset?: string;
  ramMB?: number;
  storageMB?: number;
  otgDeclared?: boolean;
  cameras?: CameraEntry[];
}

export function subscribeToUsbControllers(
  callback: (controllers: UsbController[]) => void
): Unsubscribe {
  const colRef = collection(db, "usbControllers");
  return onSnapshot(colRef, (snap) => {
    const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as UsbController);
    items.sort((a, b) => a.name.localeCompare(b.name));
    callback(items);
  });
}

export function subscribeToUsbHubs(
  callback: (hubs: UsbHub[]) => void
): Unsubscribe {
  const colRef = collection(db, "usbHubs");
  return onSnapshot(colRef, (snap) => {
    const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as UsbHub);
    items.sort((a, b) => a.name.localeCompare(b.name));
    callback(items);
  });
}

export function subscribeToUsbDevices(
  callback: (devices: UsbDevice[]) => void
): Unsubscribe {
  const colRef = collection(db, "deviceCatalog");
  return onSnapshot(colRef, (snap) => {
    const items = snap.docs.map((d) => {
      const data = d.data();
      return {
        id: d.id,
        manufacturer: data.manufacturer || "",
        model: data.model || "",
        marketingName: data.marketingName || undefined,
        controller: data.usb?.controller || "",
        androidVersion: data.androidVersion || "",
        lastSeenAt: data.lastSeenAt,
        chipset: data.chipset,
        ramMB: data.ramMB,
        storageMB: data.storageMB,
        otgDeclared: data.otgDeclared,
        cameras: data.cameras || [],
      } as UsbDevice;
    });
    items.sort((a, b) => `${a.manufacturer} ${a.model}`.localeCompare(`${b.manufacturer} ${b.model}`));
    callback(items);
  });
}

export async function updateControllerNotes(
  controllerId: string,
  notes: string
): Promise<void> {
  const docRef = doc(db, "usbControllers", controllerId);
  await setDoc(docRef, { notes }, { merge: true });
}

/**
 * Set the admin compatibility verdict for a controller (good / bad / unrated).
 * Pass null to clear the verdict (unrated).
 */
export async function updateControllerCompatible(
  controllerId: string,
  compatible: boolean | null
): Promise<void> {
  const docRef = doc(db, "usbControllers", controllerId);
  await setDoc(docRef, { compatible }, { merge: true });
}

/** Global compatibility config (usbConfig/compat). */
export interface UsbCompatConfig {
  catalogComplete: boolean;
}

export function subscribeToUsbCompatConfig(
  callback: (config: UsbCompatConfig) => void
): Unsubscribe {
  const docRef = doc(db, "usbConfig", "compat");
  return onSnapshot(docRef, (snap) => {
    const data = snap.data();
    callback({ catalogComplete: data?.catalogComplete === true });
  });
}

export async function setCatalogComplete(complete: boolean): Promise<void> {
  const docRef = doc(db, "usbConfig", "compat");
  await setDoc(docRef, { catalogComplete: complete }, { merge: true });
}

export async function getUsbCompatConfig(): Promise<UsbCompatConfig> {
  const snap = await getDoc(doc(db, "usbConfig", "compat"));
  const data = snap.data();
  return { catalogComplete: data?.catalogComplete === true };
}

export async function createUsbHub(hubId: string, data: Omit<UsbHub, "id">): Promise<void> {
  const docRef = doc(db, "usbHubs", hubId);
  await setDoc(docRef, data);
}

export async function updateUsbHub(hubId: string, data: Partial<Omit<UsbHub, "id">>): Promise<void> {
  const docRef = doc(db, "usbHubs", hubId);
  await setDoc(docRef, data, { merge: true });
}

export async function deleteUsbHub(hubId: string): Promise<void> {
  const docRef = doc(db, "usbHubs", hubId);
  await deleteDoc(docRef);
}
